import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../../components/base/PageMeta';
import JsonLd from '../../../components/base/JsonLd';
import FloatingCtaBar from '../../../components/feature/FloatingCtaBar';
import { ecommerceTools } from '../../../mocks/ecommerce-tools';

const relatedCategories = [
  { icon: 'ri-robot-2-line', grad: 'from-violet-500 to-purple-600', name: 'Software – SaaS – AI', desc: 'AI tools and SaaS platforms to automate and grow your business', count: 54, href: '/categories/software-saas-ai' },
  { icon: 'ri-line-chart-line', grad: 'from-emerald-500 to-teal-600', name: 'Finance – Investment', desc: 'Prop trading, crypto exchanges, and forex platforms', count: 25, href: '/categories/finance-investment' },
  { icon: 'ri-graduation-cap-line', grad: 'from-sky-500 to-blue-600', name: 'Education & Learning', desc: 'Online courses, learning platforms and certifications', count: 94, href: '/categories' },
];

type FilterTab = 'all' | 'free' | 'popular';
type SortOption = 'highest_rating' | 'most_reviews' | 'default';

export default function EcommerceShoppingPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [sortBy, setSortBy] = useState<SortOption>('highest_rating');
  const [sortOpen, setSortOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const sortRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';
  const sortLabels: Record<SortOption, string> = { highest_rating: 'Highest rating', most_reviews: 'Most reviews', default: 'Default' };
  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org', '@type': 'CollectionPage',
    name: 'Ecommerce & Shopping Platforms | 247digitrend',
    url: `${siteUrl}/categories/ecommerce-shopping`,
    description: 'Discover the best ecommerce and shopping platforms: dropshipping tools, online stores, fashion brands, and marketplaces.',
    breadcrumb: { '@type': 'BreadcrumbList', itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
      { '@type': 'ListItem', position: 3, name: 'Ecommerce & Shopping', item: `${siteUrl}/categories/ecommerce-shopping` },
    ]},
  };

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = ecommerceTools
    .filter(t => {
      const isFree = t.price.toLowerCase().startsWith('free');
      if (filterTab === 'free') return isFree;
      if (filterTab === 'popular') return t.reviewCount >= 1500;
      return true;
    })
    .sort((a, b) => { if (sortBy === 'highest_rating') return b.rating - a.rating; if (sortBy === 'most_reviews') return b.reviewCount - a.reviewCount; return 0; });

  const renderStars = (r: number) => Array.from({ length: 5 }).map((_, i) => {
    if (i < Math.floor(r)) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
    if (i === Math.floor(r) && r % 1 >= 0.5) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
    return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
  });

  return (
    <div className="min-h-screen" style={{ background: '#fffbf0', fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Best Ecommerce & Shopping Platforms (2026) – Dropshipping, Marketplaces & Brands | 247digitrend"
        description="Compare the best ecommerce platforms in 2026. CJDropshipping, Spocket, AliExpress, AutoDS, Feelingirl, Binance reviews with ratings and honest analysis."
        canonical={`${siteUrl}/categories/ecommerce-shopping`}
        keywords="best dropshipping platforms 2026, ecommerce tools comparison, online shopping brands, CJDropshipping review, Spocket review, AutoDS review"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer"><i className="ri-close-line text-gray-600"></i></button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-orange-100"><i className="ri-check-line text-orange-600 text-2xl"></i></div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500"><i className="ri-shopping-cart-2-line text-white text-base"></i></div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247digitrend</p>
                </div>
                <form onSubmit={e => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div><label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={e => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-orange-400" /></div>
                  <div><label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={e => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-orange-400" /></div>
                  <button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white/90 border-b border-orange-100'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[15px] font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[15px] font-semibold text-orange-500 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[15px] font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[15px] font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[15px] font-medium text-gray-700 hover:text-orange-500 transition-colors cursor-pointer">Blog</a>
            <button className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer"><i className="ri-search-line text-gray-500 text-sm"></i></button>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map(lang => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold cursor-pointer ${currentLang === lang ? 'bg-orange-50 text-orange-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span><span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90">Sign Up</button>
          </div>
        </div>
      </header>

      <main className="pt-28 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <nav className="flex items-center gap-2 mb-6">
            <a href="/" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-orange-100 hover:border-orange-300 cursor-pointer"><i className="ri-home-4-line text-gray-500 text-xs"></i></a>
            <i className="ri-arrow-right-s-line text-gray-400 text-sm"></i>
            <a href="/categories" className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-orange-100 hover:border-orange-300 cursor-pointer"><i className="ri-apps-2-line text-gray-500 text-xs"></i></a>
          </nav>

          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1 h-6 rounded-full bg-gradient-to-b from-orange-500 to-amber-500"></div>
              <span className="text-xs font-bold text-orange-600 uppercase tracking-widest">Ecommerce Category</span>
            </div>
            <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Ecommerce &amp; Shopping</h1>
            <p className="text-sm text-gray-500 mb-4">Dropshipping platforms, online marketplaces, fashion brands, and e-commerce tools for modern sellers</p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 bg-white border border-orange-200 rounded-full px-3 py-1.5">
                <i className="ri-store-2-line text-orange-500 text-xs"></i>
                <span className="text-xs font-semibold text-gray-700">{ecommerceTools.length} platforms</span>
              </div>
              <div className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                <i className="ri-star-fill text-amber-500 text-xs"></i>
                <span className="text-xs font-semibold text-amber-700">Highly rated</span>
              </div>
              <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 rounded-full px-3 py-1.5">
                <i className="ri-verified-badge-line text-orange-600 text-xs"></i>
                <span className="text-xs font-semibold text-orange-700">Verified platforms</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center bg-white border border-orange-200 rounded-full px-1 py-1 gap-0.5">
              {(['all', 'free', 'popular'] as FilterTab[]).map(tab => (
                <button key={tab} onClick={() => setFilterTab(tab)} className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap capitalize ${filterTab === tab ? 'bg-orange-500 text-white' : 'text-gray-500 hover:text-gray-700'}`}>
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
            <div className="relative" ref={sortRef}>
              <button onClick={() => setSortOpen(!sortOpen)} className="flex items-center gap-2 bg-white border border-orange-200 rounded-full px-4 py-2 text-xs font-semibold text-gray-700 hover:border-orange-300 cursor-pointer whitespace-nowrap">
                <span>Sort by: {sortLabels[sortBy]}</span>
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {sortOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-44 z-10" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
                  {(Object.entries(sortLabels) as [SortOption, string][]).map(([key, label]) => (
                    <button key={key} onClick={() => { setSortBy(key); setSortOpen(false); }} className={`w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold cursor-pointer text-left ${sortBy === key ? 'bg-orange-50 text-orange-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      {sortBy === key && <i className="ri-check-line text-orange-600"></i>}
                      <span className={sortBy === key ? '' : 'ml-4'}>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            {filtered.map((tool, i) => (
              <div key={i} className="bg-white rounded-2xl border border-orange-100 p-5 hover:border-orange-300 transition-all group">
                <div className="flex items-start gap-4">
                  {tool.logoUrl ? (
                    <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-white border border-gray-100 overflow-hidden flex items-center justify-center p-1">
                      <img src={tool.logoUrl} alt={tool.name} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className={`w-12 h-12 flex items-center justify-center flex-shrink-0 rounded-xl bg-gradient-to-br ${tool.logoGrad}`}>
                      <i className={`${tool.logoIcon} text-white text-xl`}></i>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <h3 className="text-base font-bold text-gray-900">{tool.name}</h3>
                          {tool.badge && <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tool.badgeColor}`}>{tool.badge}</span>}
                        </div>
                        <p className="text-xs font-medium text-orange-600 mb-1.5">{tool.tagline}</p>
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">{tool.intro[0].substring(0, 160)}...</p>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {tool.quickInfo.category.split(' & ').map((tag, ti) => <span key={ti} className="text-xs font-medium text-orange-700 bg-orange-50 rounded-full px-2.5 py-0.5">{tag}</span>)}
                          {tool.pros.slice(0, 1).map((p, pi) => <span key={pi} className="text-xs font-medium text-amber-700 bg-amber-50 rounded-full px-2.5 py-0.5">{p.split(' ').slice(0, 4).join(' ')}</span>)}
                        </div>
                        <p className="text-xs text-gray-500"><i className="ri-price-tag-3-line mr-1 text-gray-400"></i>{tool.price}</p>
                      </div>
                      <div className="flex flex-col items-end gap-3 flex-shrink-0">
                        <div className="text-right">
                          <div className="flex items-center gap-1 justify-end mb-0.5">
                            <div className="flex items-center gap-0.5">{renderStars(tool.rating)}</div>
                            <span className="text-sm font-bold text-gray-800 ml-0.5">{tool.rating}</span>
                          </div>
                          <span className="text-xs text-gray-400">({tool.reviewCount.toLocaleString()} reviews)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <a href={`/categories/ecommerce-shopping/${tool.slug}`} className="flex items-center gap-1.5 px-3.5 py-2 border border-gray-300 text-gray-600 hover:border-orange-400 hover:text-orange-600 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap">View Details</a>
                          <a href={tool.link} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-1.5 px-3.5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-external-link-line text-xs"></i>Access
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-orange-100">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-orange-50"><i className="ri-inbox-line text-2xl text-orange-400"></i></div>
                <p className="text-sm font-semibold text-gray-500">No platforms found</p>
                <p className="text-xs text-gray-400 mt-1">Try a different filter</p>
              </div>
            )}
          </div>

          <div className="mt-12">
            <h2 className="text-lg font-bold text-gray-900 mb-5">Related categories</h2>
            <div className="grid grid-cols-3 gap-4">
              {relatedCategories.map((cat, i) => (
                <a key={i} href={cat.href} className="bg-white rounded-2xl border border-orange-100 p-5 hover:border-orange-300 transition-all cursor-pointer group">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.grad} mb-3`}><i className={`${cat.icon} text-white text-lg`}></i></div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1 group-hover:text-orange-600 transition-colors">{cat.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3">{cat.desc}</p>
                  <div className="flex items-center gap-1 text-xs text-gray-400"><i className="ri-tools-line"></i><span>{cat.count} tools</span></div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </main>

      <section style={{ background: 'linear-gradient(135deg, #7c2d12 0%, #9a3412 100%)' }} className="py-10">
        <div className="max-w-4xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-8 text-center">
            {[{ num: '20', label: 'Platforms' }, { num: '4', label: 'Dropshipping Tools' }, { num: '12', label: 'Fashion Brands' }, { num: '500K+', label: 'Sellers & Shoppers' }].map((stat, i) => (
              <div key={i}><div className="text-4xl font-black text-white mb-1">{stat.num}</div><div className="text-sm font-medium text-orange-300">{stat.label}</div></div>
            ))}
          </div>
        </div>
      </section>

      <footer style={{ background: '#fffbf0' }} className="pt-14 pb-6 border-t border-orange-100">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-10 pb-10 border-b border-orange-100">
            <div className="md:col-span-4">
              <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain mb-4" />
              <p className="text-sm text-gray-500 leading-relaxed">247digitrend provides unbiased reviews of ecommerce platforms, dropshipping tools, and online shopping brands.</p>
            </div>
            <div className="md:col-span-2 md:col-start-6">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Resources</h5>
              {[{ label: 'All Reviews', href: '/reviews' }, { label: 'Comparisons', href: '/comparisons' }, { label: 'Categories', href: '/categories' }, { label: 'Blog', href: '/blog' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-orange-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Company</h5>
              {[{ label: 'About Us', href: '/about' }, { label: 'Contact', href: '/contact' }, { label: 'Submit a Tool', href: '/submit-tool' }, { label: 'Advertise', href: '/advertise' }].map((item, i) => (
                <a key={i} href={item.href} className="block text-sm text-gray-500 hover:text-orange-600 transition-colors mb-3 cursor-pointer">{item.label}</a>
              ))}
            </div>
            <div className="md:col-span-2">
              <h5 className="text-sm font-bold text-gray-800 mb-4">Follow Us</h5>
              <div className="flex items-center gap-3">
                {['ri-twitter-x-line', 'ri-linkedin-line', 'ri-facebook-line', 'ri-instagram-line'].map((icon, i) => (
                  <a key={i} href="#" className="w-9 h-9 flex items-center justify-center rounded-full border border-orange-200 bg-white text-gray-500 hover:text-orange-600 hover:border-orange-300 transition-all cursor-pointer"><i className={`${icon} text-base`}></i></a>
                ))}
              </div>
            </div>
          </div>
          <div className="pt-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-400">© 2026 247digitrend. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href="#" className="text-xs text-gray-400 hover:text-orange-600 cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-xs text-gray-400 hover:text-orange-600 cursor-pointer">Terms of Service</a>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-orange-600 cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
